import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TalentPoolAnalyzer = ({ skillsData, salaryData }) => {
  const [activeView, setActiveView] = useState('skills');

  const COLORS = ['#2563eb', '#d4af37', '#28a745', '#ffc107', '#dc3545'];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-executive">
          <p className="font-medium text-text-primary">{data.name}</p>
          <p className="text-sm text-text-secondary">
            {activeView === 'skills' ? `${data.value}% of talent pool` : `₹${data.value}L average`}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="500"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="card-elevated p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-headline font-semibold text-xl text-text-primary">
          Talent Pool Analyzer
        </h3>
        <div className="flex items-center space-x-2">
          <Button
            variant={activeView === 'skills' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveView('skills')}
          >
            Skills Distribution
          </Button>
          <Button
            variant={activeView === 'salary' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveView('salary')}
          >
            Salary Ranges
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart */}
        <div className="h-80 flex items-center justify-center">
          <PieChart width={400} height={320}>
              <Pie
                data={activeView === 'skills' ? skillsData : salaryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {(activeView === 'skills' ? skillsData : salaryData).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
        </div>

        {/* Legend and Details */}
        <div className="space-y-4">
          <h4 className="font-medium text-text-primary">
            {activeView === 'skills' ? 'Top Skills in Demand' : 'Salary Distribution'}
          </h4>
          
          <div className="space-y-3">
            {(activeView === 'skills' ? skillsData : salaryData).map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/30 transition-colors">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <span className="font-medium text-text-primary">{item.name}</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-text-primary">
                    {activeView === 'skills' ? `${item.value}%` : `₹${item.value}L`}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {activeView === 'skills' ? 'of talent pool' : 'average salary'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Insights */}
          <div className="mt-6 p-4 bg-cta/5 border border-cta/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="TrendingUp" size={20} color="var(--color-cta)" />
              <div>
                <h5 className="font-medium text-text-primary mb-1">Market Insight</h5>
                <p className="text-sm text-text-secondary">
                  {activeView === 'skills' 
                    ? `${skillsData[0].name} professionals are in highest demand, representing ${skillsData[0].value}% of the talent pool.`
                    : `Most professionals earn between ₹${salaryData[1].value}L - ₹${salaryData[2].value}L annually.`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentPoolAnalyzer;